import { findAll } from "./dom";

/**
 * Fills the hidden tail of a gallery the first time the reader opens it.
 *
 * <p>`loading="lazy"` does not help here. The browser only defers images it actually lays
 * out, so a lazy image inside a closed `<details>` has no box to defer against and is
 * fetched straight away. Measured in Chrome: all 21 hidden tiles on the wedding article
 * were loaded although the disclosure was shut.</p>
 *
 * <p>The tiles are therefore rendered as empty placeholders carrying the image data in
 * `data-` attributes, and the real `<img>` is built on the first open. Without JavaScript
 * the `<noscript>` twin in the markup renders instead, so no reader loses the pictures.</p>
 */
export function initGalleryMore(): void {
  for (const details of findAll<HTMLDetailsElement>("details.gallery-more")) {
    details.addEventListener("toggle", () => {
      if (details.open) {
        fill(details);
      }
    });
  }
}

/** Replaces every placeholder inside the disclosure with a real, lazily loaded image. */
function fill(details: HTMLDetailsElement): void {
  for (const slot of findAll<HTMLElement>("[data-tile-src]", details)) {
    const img = document.createElement("img");
    // sizes before srcset: otherwise the browser picks its candidate against the default
    // 100vw and fetches a wider rendition than the tile needs.
    img.loading = "lazy";
    img.sizes = `auto, ${slot.dataset.tileSizes ?? "100vw"}`;
    img.srcset = slot.dataset.tileSrcset ?? "";
    img.src = slot.dataset.tileSrc ?? "";
    img.width = Number(slot.dataset.tileWidth ?? 0);
    img.height = Number(slot.dataset.tileHeight ?? 0);
    img.alt = slot.dataset.tileAlt ?? "";
    img.decoding = "async";
    slot.replaceWith(img);
  }
}
